import { useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { api } from '../../lib/api'
import { useAuthStore, type AppRole } from '../../stores/auth'
import { normalizePhone, isValidNormalizedPhone } from '@velorent/shared'

type RoleSelectState = {
  tempToken: string
  availableRoles: AppRole[]
  user: { id: string; name: string; phone: string; tenantId: string; timezone?: string }
}

const ROLE_LABEL: Record<AppRole, string> = { owner: 'Владелец', manager: 'Менеджер', mechanic: 'Механик' }
const ROLE_ICON: Record<AppRole, string> = { owner: '👑', manager: '💼', mechanic: '🔧' }

function routeForRole(role: AppRole): string {
  if (role === 'owner') return '/owner'
  if (role === 'mechanic') return '/mechanic'
  return '/manager'
}

export default function LoginPage() {
  const navigate = useNavigate()
  const { setAuth } = useAuthStore()
  const [error, setError] = useState<string | null>(null)
  const [loading, setLoading] = useState(false)
  const [roleSelect, setRoleSelect] = useState<RoleSelectState | null>(null)
  const phoneRef = useRef<HTMLInputElement>(null)
  const passwordRef = useRef<HTMLInputElement>(null)

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setError(null)

    const rawPhone = phoneRef.current?.value?.trim() ?? ''
    const password = passwordRef.current?.value ?? ''

    if (!rawPhone) { setError('Введите номер телефона'); return }
    if (password.length < 6) { setError('Пароль — минимум 6 символов'); return }

    const phone = normalizePhone(rawPhone)
    if (!isValidNormalizedPhone(phone)) {
      setError(`Неверный формат телефона. Ожидается: +7XXXXXXXXXX`)
      return
    }

    setLoading(true)
    try {
      const res = await api.post('/auth/login', { phone, password })
      if (res.data?.requiresRoleSelect) {
        setRoleSelect({
          tempToken: res.data.tempToken,
          availableRoles: res.data.availableRoles as AppRole[],
          user: res.data.user,
        })
        return
      }
      setAuth(res.data.user, res.data.accessToken, res.data.refreshToken)
      navigate(routeForRole(res.data.user.role as AppRole))
    } catch (err: unknown) {
      const msg = (err as { response?: { data?: { error?: { message?: string } } } })?.response?.data?.error?.message
      setError(msg ?? 'Неверный телефон или пароль')
    } finally {
      setLoading(false)
    }
  }

  const handleRolePick = async (role: AppRole) => {
    if (!roleSelect) return
    setError(null)
    setLoading(true)
    try {
      const res = await api.post('/auth/select-role', { tempToken: roleSelect.tempToken, role })
      setAuth(res.data.user, res.data.accessToken, res.data.refreshToken)
      navigate(routeForRole(res.data.user.role as AppRole))
    } catch (err: unknown) {
      const msg = (err as { response?: { data?: { error?: { message?: string } } } })?.response?.data?.error?.message
      setError(msg ?? 'Не удалось выбрать роль')
      // tempToken can expire (3 min) — go back to login on auth errors
      if ((err as { response?: { status?: number } })?.response?.status === 401) {
        setRoleSelect(null)
      }
    } finally {
      setLoading(false)
    }
  }

  return (
    <div className="min-h-screen bg-gray-50 px-4 pt-16 pb-10 flex flex-col items-center">
      <div className="w-full max-w-sm">
        <div className="text-center mb-5">
          <span className="text-4xl">🚲</span>
          <h1 className="mt-2 text-xl font-bold text-gray-900">VeloRent</h1>
          <p className="text-xs text-gray-500">Система учёта аренды</p>
        </div>

        {error && (
          <div className="mb-3 bg-red-50 border border-red-200 rounded-xl p-3 text-sm text-red-700">
            {error}
          </div>
        )}

        {!roleSelect ? (
          <div className="bg-white rounded-2xl shadow-sm border border-gray-200 p-5">
            <form onSubmit={handleSubmit} className="space-y-4" autoComplete="on">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Телефон</label>
                <input
                  ref={phoneRef}
                  name="phone"
                  type="tel"
                  autoComplete="username"
                  placeholder="+7 900 000 00 00"
                  className="w-full rounded-xl border-gray-300 shadow-sm focus:border-brand-500 focus:ring-brand-500 text-base"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Пароль</label>
                <input
                  ref={passwordRef}
                  name="password"
                  type="password"
                  autoComplete="current-password"
                  placeholder="••••••"
                  className="w-full rounded-xl border-gray-300 shadow-sm focus:border-brand-500 focus:ring-brand-500 text-base"
                />
              </div>

              <button
                type="submit"
                disabled={loading}
                className="w-full bg-brand-600 hover:bg-brand-700 disabled:opacity-60 text-white font-semibold py-3 px-4 rounded-xl transition-colors text-base"
              >
                {loading ? 'Вход...' : 'Войти'}
              </button>
            </form>
          </div>
        ) : (
          <div className="bg-white rounded-2xl shadow-sm border border-gray-200 p-5 space-y-3">
            <div className="text-center">
              <p className="text-sm font-medium text-gray-900">{roleSelect.user.name}</p>
              <p className="text-xs text-gray-500">Выберите роль для этой сессии</p>
            </div>
            <div className="space-y-2">
              {roleSelect.availableRoles.map((r) => (
                <button
                  key={r}
                  type="button"
                  onClick={() => handleRolePick(r)}
                  disabled={loading}
                  className="w-full flex items-center gap-3 px-4 py-3 rounded-xl border border-gray-300 hover:bg-gray-50 disabled:opacity-60"
                >
                  <span className="text-2xl">{ROLE_ICON[r]}</span>
                  <span className="text-base font-medium text-gray-800">{ROLE_LABEL[r]}</span>
                </button>
              ))}
            </div>
            <button
              type="button"
              onClick={() => { setRoleSelect(null); setError(null) }}
              className="w-full text-xs text-gray-500 hover:text-gray-700"
            >
              ← Вернуться к входу
            </button>
          </div>
        )}

      </div>
    </div>
  )
}
